import React, { useState, useEffect, useRef } from 'react';
import { Order, OrderStatus } from '../types';
import { CheckCircle, Clock, Calendar, Phone, MapPin, Package, Check, X, Loader } from 'lucide-react';
import { formatPhoneNumber, formatDate } from '../utils';
import * as storage from '../services/storageService';

interface OrderCardProps {
  order: Order;
  onUpdateStatus: (id: string, status: OrderStatus) => void;
  onEdit: (order: Order) => void;
  onItemToggle: (orderId: string, itemId: string) => void;
}

const OrderCard: React.FC<OrderCardProps> = ({ order, onUpdateStatus, onEdit, onItemToggle }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [orderWithDetails, setOrderWithDetails] = useState<Order>(order);
  const historyRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const isDone = order.status === 'Done';
  const isPickedUp = order.status === 'Picked Up';

  // Check if details are already loaded
  useEffect(() => {
    if (order.items && order.items.length > 0) {
      setDetailsLoaded(true);
      setOrderWithDetails(order);
    }
  }, [order]);

  // Set up Intersection Observer to load details when card is visible
  useEffect(() => {
    if (!cardRef.current || detailsLoaded || loadingDetails) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadOrderDetails();
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading when card is within 100px of viewport
    );

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [cardRef, detailsLoaded, loadingDetails]);

  // Function to load order details
  const loadOrderDetails = async () => {
    if (detailsLoaded || loadingDetails) return;

    setLoadingDetails(true);
    try {
      const details = await storage.getOrderDetails(order.id);
      if (details) {
        setOrderWithDetails(details);
        setDetailsLoaded(true);
      }
    } catch (error) {
      console.error('Error loading order details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Click outside to close history
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (historyRef.current && !historyRef.current.contains(event.target as Node)) {
        setShowHistory(false);
      }
    }
    if (showHistory) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHistory]);

  const statusColors = {
    'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Done': 'bg-green-100 text-green-800 border-green-200',
    'Picked Up': 'bg-gray-100 text-gray-800 border-gray-200',
    'Cancelled': 'bg-red-100 text-red-800 border-red-200',
    'Website Submitted': 'bg-blue-100 text-blue-800 border-blue-200',
  };


  return (
    <div ref={cardRef} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible hover:shadow-md transition-shadow flex flex-col h-full relative">
      {/* Tape Effect for Corkboard feel */}
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-red-900/10 rotate-1 z-0"></div>

      <div className="p-4 border-b border-gray-100 flex justify-between items-start pt-6">
        <div>
          <h3 
            className="text-lg font-bold text-gray-900 cursor-pointer hover:text-red-700 hover:underline"
            onClick={async () => {
              // Load details if not already loaded
              if (!detailsLoaded) {
                setLoadingDetails(true);
                try {
                  const details = await storage.getOrderDetails(order.id);
                  if (details) {
                    setOrderWithDetails(details);
                    setDetailsLoaded(true);
                    onEdit(details);
                  } else {
                    onEdit(order);
                  }
                } catch (error) {
                  console.error('Error loading order details for edit:', error);
                  onEdit(order);
                } finally {
                  setLoadingDetails(false);
                }
              } else {
                onEdit(orderWithDetails);
              }
            }}
          >
            {order.lastName}, {order.firstName}
            {loadingDetails && <Loader className="inline-block w-3 h-3 ml-2 text-gray-400 animate-spin" />}
          </h3>
          <div className="flex items-center text-sm text-gray-500 mt-1">
            <Phone className="w-3 h-3 mr-1" />
            {formatPhoneNumber(order.phone)}
          </div>
        </div>
        <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${statusColors[order.status]}`}>
          {order.status}
        </span>
      </div>

      <div className="p-4 flex-1 space-y-3">
        <div className="flex items-center text-sm text-gray-600">
           <MapPin className="w-4 h-4 mr-2 text-gray-400" />
           {order.location}
        </div>
        <div className="flex items-center text-sm text-gray-600">
           <Calendar className="w-4 h-4 mr-2 text-gray-400" />
            Pickup: {formatDate(order.pickupDate)}
        </div>

        <div className="mt-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Items</p>

          {loadingDetails ? (
            <div className="flex justify-center items-center py-4">
              <Loader className="w-5 h-5 text-gray-400 animate-spin" />
              <span className="ml-2 text-sm text-gray-500">Loading details...</span>
            </div>
          ) : !detailsLoaded ? (
            <div className="flex justify-center items-center py-4">
              <span className="text-sm text-gray-500">Details will load when visible</span>
            </div>
          ) : (
            <ul className="space-y-2">
              {orderWithDetails.items.map((item, idx) => (
                <li 
                  key={idx} 
                  className="text-sm flex items-start group cursor-pointer"
                  onClick={(e) => {
                      e.stopPropagation();
                      // Prevent toggling if order is finalized/picked up/cancelled, or allow it but don't change order status (App logic handles order status)
                      onItemToggle(order.id, item.id);
                  }}
                >
                  <div 
                      className={`mt-0.5 mr-2 flex-shrink-0 w-5 h-5 flex items-center justify-center border rounded transition-colors duration-200 ${
                          item.isDone 
                          ? 'bg-green-100 border-green-500 text-green-600' 
                          : 'bg-white border-gray-300 text-transparent hover:border-red-400'
                      }`}
                  >
                      <Check className={`w-3.5 h-3.5 ${item.isDone ? 'opacity-100' : 'opacity-0'}`} strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                      <span className={`transition-colors ${item.isDone ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                          <span className="font-bold">{item.quantity}x</span> {item.name} 
                          {item.weightMin && <span className="text-xs text-gray-500 ml-1">({item.weightMin}-{item.weightMax} lbs)</span>}
                      </span>
                      {item.notes && (
                          <div className={`text-xs mt-0.5 italic ${item.isDone ? 'text-gray-300' : 'text-red-600'}`}>
                              "{item.notes}"
                          </div>
                      )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {order.notes && (
            <div className="mt-3 bg-yellow-50 p-2 rounded text-xs text-yellow-800 italic border border-yellow-100">
                "{order.notes}"
            </div>
        )}
      </div>

      <div className="p-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center relative">
        <div className="flex flex-col">
            {order.takenBy && order.takenBy !== 'Unknown' && (
              <div className="text-xs text-gray-500 font-medium">
                  Taken by: <span className="text-gray-700">{order.takenBy}</span>
              </div>
            )}

            <div className="relative mt-1" ref={historyRef}>
                <button 
                    onClick={async (e) => { 
                        e.stopPropagation(); 
                        // If details aren't loaded yet, load them when history is opened
                        if (!showHistory && !detailsLoaded && !loadingDetails) {
                            await loadOrderDetails();
                            // Now that details are loaded, show history
                            setShowHistory(true);
                        } else {
                            // Toggle history visibility if details are already loaded
                            setShowHistory(!showHistory);
                        }
                    }}
                    className="text-[11px] text-gray-400 hover:text-red-600 underline flex items-center transition-colors"
                >
                    <Clock className="w-3 h-3 mr-1" />
                    Updated: {formatDate(order.lastUpdated || order.createdAt)}
                </button>

                {/* History Popover */}
                {showHistory && (
                    <div className="absolute left-0 bottom-full mb-2 w-72 bg-white shadow-xl rounded-lg border border-gray-200 z-50 p-3 transform origin-bottom-left animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-2 border-b border-gray-100 pb-2">
                            <h4 className="font-bold text-xs text-gray-700">Status History</h4>
                            <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100">
                                <X className="w-3 h-3"/>
                            </button>
                        </div>
                        <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                            {loadingDetails ? (
                                <div className="flex justify-center items-center py-2">
                                    <Loader className="w-4 h-4 text-gray-400 animate-spin" />
                                    <span className="ml-2 text-xs text-gray-500">Loading...</span>
                                </div>
                            ) : (
                                <>
                                    {(orderWithDetails.statusHistory || []).slice().reverse().map((h, i) => (
                                        <div key={i} className="text-xs relative pl-3 border-l border-gray-200">
                                            <div className="absolute -left-[3px] top-1.5 w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                                            <div className="flex justify-between font-semibold text-gray-800">
                                                <span>{h.status}</span>
                                                <span>{formatDate(h.timestamp)}</span>
                                            </div>
                                            <div className="text-gray-400 text-[10px] flex justify-between mt-0.5">
                                                <span>{new Date(h.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                <span>By: {h.updatedBy}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {(!orderWithDetails.statusHistory || orderWithDetails.statusHistory.length === 0) && 
                                        <p className="text-xs text-gray-400 italic">No history available</p>
                                    }
                                </>
                            )}
                        </div>
                        <div className="absolute -bottom-2 left-4 w-4 h-4 bg-white border-b border-r border-gray-200 transform rotate-45"></div>
                    </div>
                )}
            </div>
        </div>

        <div className="flex space-x-2">
            {!isDone && !isPickedUp && (
                 <button 
                 onClick={() => onUpdateStatus(order.id, 'Done')}
                 className="flex items-center px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors shadow-sm"
               >
                 <CheckCircle className="w-3 h-3 mr-1" />
                 Mark Done
               </button>
            )}
             {isDone && !isPickedUp && (
                 <button 
                 onClick={() => onUpdateStatus(order.id, 'Picked Up')}
                 className="flex items-center px-3 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-800 transition-colors shadow-sm"
               >
                 <Package className="w-3 h-3 mr-1" />
                 Picked Up
               </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
